import { DeleteOutlined, PlusOutlined, TranslationOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Input, message, Row, Select, Spin, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS, getAuthHeaders } from '@/config/api';
import { apiClient } from '@/services/api-client';

const { Text } = Typography;
const { TextArea } = Input;

interface TranslationItem {
  langCode: string;
  name: string;
  description?: string | null;
}

interface LanguageOption {
  id: string;
  code: string;
  name: string;
  isDefault: boolean;
}

interface TranslationsManagerProps {
  /** The entity ID (must exist in DB before translations can be saved) */
  entityId: string;
  /** API endpoint to GET/PUT translations, e.g. (id) => `/food-categories/${id}/translations` */
  translationsEndpoint: (id: string) => string;
  /** Whether description field is relevant for this entity */
  hasDescription?: boolean;
}

const TranslationsManager: React.FC<TranslationsManagerProps> = ({
  entityId,
  translationsEndpoint,
  hasDescription = true,
}) => {
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [translations, setTranslations] = useState<TranslationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingLang, setAddingLang] = useState<string | undefined>();

  useEffect(() => {
    loadData();
  }, [entityId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [langs, trans] = await Promise.all([
        apiClient.get<LanguageOption[]>(API_ENDPOINTS.LANGUAGES.BASE + '/active'),
        apiClient.get<TranslationItem[]>(translationsEndpoint(entityId)),
      ]);
      setLanguages(langs);
      setTranslations(trans);
    } catch {
      message.error('Không thể tải dữ liệu bản dịch');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(translationsEndpoint(entityId), { translations });
      message.success('Đã lưu bản dịch');
    } catch {
      message.error('Lưu bản dịch thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleAddLanguage = () => {
    if (!addingLang) return;
    if (translations.some((t) => t.langCode === addingLang)) {
      message.warning('Ngôn ngữ này đã có bản dịch');
      return;
    }
    setTranslations((prev) => [...prev, { langCode: addingLang, name: '', description: null }]);
    setAddingLang(undefined);
  };

  const handleRemove = (langCode: string) => {
    setTranslations((prev) => prev.filter((t) => t.langCode !== langCode));
  };

  const handleChange = (langCode: string, field: 'name' | 'description', value: string) => {
    setTranslations((prev) =>
      prev.map((t) => (t.langCode === langCode ? { ...t, [field]: value } : t)),
    );
  };

  const availableLangsToAdd = languages.filter(
    (l) => !translations.some((t) => t.langCode === l.code),
  );

  const getLangName = (code: string) =>
    languages.find((l) => l.code === code)?.name ?? code;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin />
      </div>
    );
  }

  return (
    <Card
      size="small"
      title={
        <span>
          <TranslationOutlined style={{ marginRight: 8 }} />
          Quản lý bản dịch
        </span>
      }
      extra={
        <Button type="primary" size="small" loading={saving} onClick={handleSave}>
          Lưu bản dịch
        </Button>
      }
    >
      {translations.length === 0 && (
        <Text type="secondary">Chưa có bản dịch nào. Thêm ngôn ngữ bên dưới.</Text>
      )}

      {translations.map((t) => (
        <div key={t.langCode} style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 6 }}>
            <Col>
              <Text strong>{getLangName(t.langCode)}</Text>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                ({t.langCode})
              </Text>
            </Col>
            <Col>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleRemove(t.langCode)}
              />
            </Col>
          </Row>
          <Input
            placeholder="Tên *"
            value={t.name}
            onChange={(e) => handleChange(t.langCode, 'name', e.target.value)}
            style={{ marginBottom: hasDescription ? 8 : 0 }}
          />
          {hasDescription && (
            <TextArea
              placeholder="Mô tả (tùy chọn)"
              value={t.description ?? ''}
              onChange={(e) => handleChange(t.langCode, 'description', e.target.value)}
              rows={2}
            />
          )}
          <Divider style={{ margin: '12px 0' }} />
        </div>
      ))}

      <Row gutter={8} align="middle">
        <Col flex="auto">
          <Select
            placeholder="Chọn ngôn ngữ để thêm"
            style={{ width: '100%' }}
            value={addingLang}
            onChange={setAddingLang}
            options={availableLangsToAdd.map((l) => ({
              label: `${l.name} (${l.code})`,
              value: l.code,
            }))}
          />
        </Col>
        <Col>
          <Button
            icon={<PlusOutlined />}
            onClick={handleAddLanguage}
            disabled={!addingLang}
          >
            Thêm
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default TranslationsManager;
